<template>
    <vx-table-row>
        <vx-table-row-title class="wrap-break-word text-sm py-0! xl:max-w-md">
            {{ props.agent.name }}
            <div v-if="props.agent.description" class="text-xs text-slate-500 font-normal mt-1">
                {{ props.agent.description }}
            </div>
        </vx-table-row-title>

        <vx-table-row-item data-test="targets">
            <template v-slot:label>Brand Standards:</template>
            <font-awesome-icon
                v-if="messageStandardCounts[props.agent.id] == undefined"
                widthAuto
                :icon="faLoader"
                spin
            ></font-awesome-icon>

            <vx-popover
                v-else
                arrow
                hover
                placement="top"
                class="cursor-pointer"
                @click="copyText(messageStandardCounts[props.agent.id] ?? 0)"
            >
                <span>
                    {{ formatCount(messageStandardCounts[props.agent.id] ?? 0) }}
                </span>
                <template v-slot:content>
                    <div class="text-center">
                        {{ formatNumber(messageStandardCounts[props.agent.id] ?? 0) }}
                        <div class="text-xs pt-1 text-slate-500">Click to copy</div>
                    </div>
                </template>
            </vx-popover>
        </vx-table-row-item>

        <vx-table-row-item data-test="validation">
            <template v-slot:label>Validation:</template>
            {{ capitalize(props.agent.validation) }}
        </vx-table-row-item>

        <vx-table-row-buttons v-if="props.isAdminOrOwner">
            <vx-dropdown placement="right">
                <vx-button data-test="dropdown" type="button" color="muted" outline class="w-[45px]">
                    <font-awesome-icon widthAuto :icon="faEllipsis" />
                </vx-button>

                <template v-slot:items>
                    <vx-dropdown-item data-test="delete" @click="deleteAgent()">
                        Delete
                        <font-awesome-icon widthAuto :icon="faTrash"></font-awesome-icon>
                    </vx-dropdown-item>
                </template>
            </vx-dropdown>

            <vx-button
                :href="`/settings/teams/${teamId}/ai-agents/message-vetting/${props.agent.id}`"
                data-test="edit"
                outline
                color="muted"
            >
                Edit <font-awesome-icon widthAuto :icon="faFilePen" />
            </vx-button>
        </vx-table-row-buttons>
    </vx-table-row>
</template>

<script setup lang="ts">
import { faEllipsis, faFilePen, faLoader, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import {
    VxButton,
    VxDropdown,
    VxDropdownItem,
    VxPopover,
    VxSwal,
    VxTableRow,
    VxTableRowButtons,
    VxTableRowItem,
    VxTableRowTitle,
} from '@voxie/frontend-components';
import type { AiMessageVettingAgent } from '~/services/aiMessageVettingAgents/aiMessageVettingAgentsClient.types';
import { aiMessageVettingAgentsClient } from '~/services';
import { throwSwalRequestError } from '~/utils/request';
import { capitalize, formatNumber } from '~/utils/helpers';
import { useAiMessageVettingAgentsMessageStandardCounts } from '~/composables/useAiMessageVettingAgentsMessageStandardCounts';
import { useCopyText } from '~/composables/useCopyText';
import { formatCount } from '~/components/filters';

const teamId = Spark.state.currentTeam.id;

const props = defineProps<{
    agent: AiMessageVettingAgent;
    isAdminOrOwner?: boolean;
}>();

const emit = defineEmits<{
    deleted: [agentId: number];
}>();

const { messageStandardCounts } = useAiMessageVettingAgentsMessageStandardCounts();
const { copyText } = useCopyText();

const deleteAgent = () => {
    VxSwal.confirmation('Delete Agent', 'Are you sure? There is no undo.', 'danger', () =>
        aiMessageVettingAgentsClient
            .deleteAiMessageVettingAgent({ teamId }, props.agent.id)
            .then(() => emit('deleted', props.agent.id))
            .catch(throwSwalRequestError)
    );
};
</script>
